<script>
import ClockFace from './clock-face.vue'

const pad = (n) => String(n).padStart(2, '0')

const parseTime = (val) => {
  if (!val || typeof val !== 'string') return { hours: 0, minutes: 0 }
  const match = val.match(/^(\d{1,2}):(\d{2})$/)
  if (!match) return { hours: 0, minutes: 0 }
  return {
    hours: Math.min(Number(match[1]), 23),
    minutes: Math.min(Number(match[2]), 59),
  }
}

const formatTime = (hours, minutes) => pad(hours) + ':' + pad(minutes)

export default {
  name: 'ClockTimePicker',
  components: { ClockFace },
  inheritAttrs: false,
  props: {
    value: {
      type: String,
      default: '',
    },
    label: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    rules: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      menuOpen: false,
      menuX: 0,
      menuY: 0,
      clockMode: 'hours',
      innerHours: 0,
      innerMinutes: 0,
      editingField: null,
      editBuffer: '',
    }
  },
  computed: {
    displayValue() {
      const parsed = parseTime(this.value)
      const h = pad(parsed.hours)
      const m = pad(parsed.minutes)

      // Mostra dígito parcial durante digitação
      if (this.editBuffer.length === 1) {
        if (this.editingField === 'hours') {
          return '0' + this.editBuffer + ':' + m
        }
        if (this.editingField === 'minutes') {
          return h + ':0' + this.editBuffer
        }
      }

      return h + ':' + m
    },
    headerHours() {
      return pad(this.innerHours)
    },
    headerMinutes() {
      return pad(this.innerMinutes)
    },
    clockValue() {
      return this.clockMode === 'hours' ? this.innerHours : this.innerMinutes
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(val) {
        const parsed = parseTime(val)
        this.innerHours = parsed.hours
        this.innerMinutes = parsed.minutes
      },
    },
    menuOpen(open) {
      if (!open) {
        const result = formatTime(this.innerHours, this.innerMinutes)
        if (result !== this.value) {
          this.$emit('input', result)
        }
      }
    },
  },
  methods: {
    openMenu() {
      if (this.disabled || this.menuOpen) return
      const parsed = parseTime(this.value)
      this.innerHours = parsed.hours
      this.innerMinutes = parsed.minutes
      this.clockMode = 'hours'
      this._positionMenu()
      this.menuOpen = true
    },

    _positionMenu() {
      const ref = this.$refs.timeInput
      const el = ref && ref.$el
      if (!el) return
      const rect = el.getBoundingClientRect()
      this.menuX = rect.left
      this.menuY = rect.bottom
    },

    selectClockValue(val) {
      if (this.clockMode === 'hours') {
        this.innerHours = val
      } else {
        this.innerMinutes = val
      }
    },

    handleDragEnd() {
      if (this.clockMode === 'hours') {
        this.$nextTick(() => {
          this.clockMode = 'minutes'
        })
      } else {
        this._emitAndClose()
      }
    },

    setModeHours() {
      this.clockMode = 'hours'
    },

    setModeMinutes() {
      this.clockMode = 'minutes'
    },

    _emitAndClose() {
      const result = formatTime(this.innerHours, this.innerMinutes)
      this.$emit('input', result)
      this.menuOpen = false
    },

    // ─── Input field helpers ────────────────────────────
    _getInputEl() {
      const ref = this.$refs.timeInput
      return ref && ref.$el ? ref.$el.querySelector('input') : null
    },

    _selectField(field) {
      this.editingField = field
      this.editBuffer = ''
      const input = this._getInputEl()
      if (!input) return
      const range = field === 'hours' ? [0, 2] : [3, 5]
      this.$nextTick(() => {
        input.focus()
        if (input.setSelectionRange) input.setSelectionRange(range[0], range[1])
      })
    },

    handleFieldFocus() {
      this._selectField('hours')
    },

    handleFieldClick() {
      if (!this.menuOpen) {
        this.openMenu()
      }
      const input = this._getInputEl()
      if (!input) return
      const pos = input.selectionStart || 0
      this._selectField(pos <= 2 ? 'hours' : 'minutes')
    },

    handleManualInput(val) {
      // Impede que o v-text-field altere o valor livremente.
      // Força o valor de volta ao displayValue para manter o formato HH:mm.
      // A edição real é controlada via handleKeyDown.
      const input = this._getInputEl()
      if (input) {
        input.value = this.displayValue
      }
    },

    handleKeyDown(event) {
      const key = event.key
      if (key === 'Tab') return
      event.preventDefault()

      if (key === 'Backspace' || key === 'Delete') {
        this._handleBackspace()
      } else if (key === 'ArrowUp' || key === 'ArrowDown') {
        this._handleArrowKey(key)
      } else if (key === 'ArrowLeft' || key === 'ArrowRight') {
        this._handleHorizontalArrow(key)
      } else if (key >= '0' && key <= '9') {
        this._handleDigit(key)
      }
    },

    _handleBackspace() {
      const parsed = parseTime(this.value)
      if (this.editingField === 'hours') {
        parsed.hours = 0
      } else {
        parsed.minutes = 0
      }
      this.editBuffer = ''
      this.$emit('input', formatTime(parsed.hours, parsed.minutes))
      this._selectField(this.editingField)
    },

    _handleArrowKey(key) {
      const delta = key === 'ArrowUp' ? 1 : -1
      const parsed = parseTime(this.value)

      if (this.editingField === 'hours') {
        parsed.hours = (parsed.hours + delta + 24) % 24
      } else {
        parsed.minutes = (parsed.minutes + delta + 60) % 60
      }

      this.$emit('input', formatTime(parsed.hours, parsed.minutes))
      this._selectField(this.editingField)
    },

    _handleHorizontalArrow(key) {
      if (key === 'ArrowRight' && this.editingField === 'hours') {
        this._selectField('minutes')
      } else if (key === 'ArrowLeft' && this.editingField === 'minutes') {
        this._selectField('hours')
      }
    },

    _handleDigit(digit) {
      this.editBuffer += digit
      if (this.editingField === 'hours') {
        this._applyHoursDigit()
      } else {
        this._applyMinutesDigit()
      }
    },

    _emitAndSelect(parsed, field) {
      this.editBuffer = ''
      this.$emit('input', formatTime(parsed.hours, parsed.minutes))
      this._selectField(field)
    },

    _applyHoursDigit() {
      const parsed = parseTime(this.value)

      if (this.editBuffer.length === 1) {
        if (Number(this.editBuffer) > 2) {
          parsed.hours = Number(this.editBuffer)
          this._emitAndSelect(parsed, 'minutes')
        }
        return
      }

      parsed.hours = Math.min(Number(this.editBuffer), 23)
      this._emitAndSelect(parsed, 'minutes')
    },

    _applyMinutesDigit() {
      const parsed = parseTime(this.value)

      if (this.editBuffer.length === 1) {
        if (Number(this.editBuffer) > 5) {
          parsed.minutes = Number(this.editBuffer)
          this._emitAndSelect(parsed, 'minutes')
        }
        return
      }

      parsed.minutes = Math.min(Number(this.editBuffer), 59)
      this._emitAndSelect(parsed, 'minutes')
    },
  },
}
</script>

<template>
  <div class="clock-time-picker">
    <v-text-field
      ref="timeInput"
      :value="displayValue"
      :label="label"
      :disabled="disabled"
      :rules="rules"
      class="align-icon-search"
      v-bind="$attrs"
      @focus="handleFieldFocus"
      @click.stop="handleFieldClick"
      @keydown="handleKeyDown"
      @input="handleManualInput"
    >
      <template v-slot:prepend-inner>
        <icon-svg name="clock" size="18" color="#8e8e8e" />
      </template>
    </v-text-field>

    <v-menu
      v-model="menuOpen"
      :close-on-content-click="false"
      :position-x="menuX"
      :position-y="menuY"
      absolute
      offset-y
      transition="scroll-y-transition"
      max-width="260"
      min-width="260"
    >
      <div class="clock-time-picker__dropdown">
        <!-- Header: HH : mm -->
        <div
          class="clock-time-picker__header d-flex align-center justify-center"
        >
          <div
            class="clock-time-picker__header-segment"
            :class="{
              'clock-time-picker__header-segment--active':
                clockMode === 'hours',
            }"
            @click="setModeHours"
          >
            {{ headerHours }}
          </div>
          <span class="clock-time-picker__header-separator">:</span>
          <div
            class="clock-time-picker__header-segment"
            :class="{
              'clock-time-picker__header-segment--active':
                clockMode === 'minutes',
            }"
            @click="setModeMinutes"
          >
            {{ headerMinutes }}
          </div>
        </div>

        <!-- Clock face -->
        <div class="clock-time-picker__clock-container">
          <ClockFace
            :mode="clockMode"
            :value="clockValue"
            @input="selectClockValue"
            @drag-end="handleDragEnd"
          />
        </div>
      </div>
    </v-menu>
  </div>
</template>

<style lang="sass" scoped>
.clock-time-picker
  position: relative

.clock-time-picker__dropdown
  background: white
  border-radius: 8px
  padding: 16px
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12)

.clock-time-picker__header
  margin-bottom: 16px

.clock-time-picker__header-segment
  font-family: var(--font-family-paragraph)
  font-size: 32px
  font-weight: 600
  color: #999
  padding: 4px 8px
  border-radius: 8px
  cursor: pointer
  transition: background-color 0.15s ease, color 0.15s ease
  line-height: 1

  &--active
    background: var(--primary)
    color: white

.clock-time-picker__header-separator
  font-family: var(--font-family-paragraph)
  font-size: 32px
  font-weight: 600
  color: #999
  margin: 0 4px
  line-height: 1

.clock-time-picker__clock-container
  display: flex
  justify-content: center
</style>
